// Spec 37. Convex hull + community tint helpers for the graph overlay.
//
// Used to draw a faint coloured wash behind each community's member nodes so
// groups read at a glance, without moving or recolouring the nodes themselves.

export interface Point {
	x: number;
	y: number;
}

// Andrew's monotone-chain convex hull. Returns the hull points in order.
// Fewer than 3 points have no area; return them as-is.
export function convexHull(points: Point[]): Point[] {
	if (points.length < 3) return points.slice();
	const pts = [...points].sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
	const cross = (o: Point, a: Point, b: Point) =>
		(a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
	const lower: Point[] = [];
	for (const p of pts) {
		while (
			lower.length >= 2 &&
			cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0
		)
			lower.pop();
		lower.push(p);
	}
	const upper: Point[] = [];
	for (let i = pts.length - 1; i >= 0; i--) {
		const p = pts[i]!;
		while (
			upper.length >= 2 &&
			cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0
		)
			upper.pop();
		upper.push(p);
	}
	lower.pop();
	upper.pop();
	return lower.concat(upper);
}

// Push each hull vertex outward from the hull's centroid by `pad` pixels so
// the wash clears the node discs it encloses.
export function padHull(hull: Point[], pad: number): Point[] {
	if (hull.length === 0) return hull;
	const cx = hull.reduce((s, p) => s + p.x, 0) / hull.length;
	const cy = hull.reduce((s, p) => s + p.y, 0) / hull.length;
	return hull.map((p) => {
		const dx = p.x - cx;
		const dy = p.y - cy;
		const d = Math.hypot(dx, dy) || 1;
		return { x: p.x + (dx / d) * pad, y: p.y + (dy / d) * pad };
	});
}

// Stable hue per community id (golden-angle-ish hash) so adjacent groups get
// distinguishable washes without a fixed palette.
export function communityHue(id: string): number {
	let h = 0;
	for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
	return h % 360;
}
